import { useState, useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AssignSupplierService,
  AssignSupplierResult,
  OrderValidationService,
} from "../assignSupplierUtils";
import { OrderItem, SupplierParts } from "../orderUtils";
import useNotification from "../../../utils/useNotification";
import { useGaraazToastWithChildren } from "../../../utils/useNotification";
import { OrdersTextLabels } from "../enums";
import OrderStatusChangeErrorBox from "../components/OrderStatusChangeErrorBox";
import { EstimateAllocatorService } from "../services/EstimateAllocatorService";
import { parseCptHoursOffset } from "../../../components/SupplierInventorySearch/utils/cptCalculationUtils";
import { SupplierPart } from "../../../components/SupplierInventorySearch/utils/partsCalculationUtils";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { useOrdersStore } from "../store/useOrdersStore";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
} from "../../../components/DeliveryOptions/types";

interface UseAssignSupplierProps {
  orderId: string;
  orderData: any;
  orderItems: OrderItem[];
  setOrderMetaInfoWithOrderId: (orderId: string, metaInfo: any) => void;
  setAssignedSupplier: (suppliers: any[]) => void;
  refetchAllOrders: () => void;
  supplierName?: string;
  isCustomerOrderFreezed: boolean;
}

interface AssignSupplierParams {
  supplierId: string;
  supplierParts?: SupplierParts[];
  supplierName: string;
  transactionType?: string;
  discount?: number;
  creditNotePercentage?: number;
  splitSelections?: NormalizedDeliverySelection[];
  cptGivenTime?: string;
  splitData?: EnrichedSupplierForDelivery[];
  estimateNo?: string;
}

export const useAssignSupplier = ({
  orderId,
  orderData,
  orderItems,
  setOrderMetaInfoWithOrderId,
  setAssignedSupplier,
  refetchAllOrders,
  supplierName,
  isCustomerOrderFreezed,
}: UseAssignSupplierProps) => {
  const [isAssigning, setIsAssigning] = useState(false);
  const toast = useNotification();
  const garaazToast = useGaraazToastWithChildren();
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const orderEta = useOrdersStore(
    useCallback((state) => state.orderEtas[orderId], [orderId])
  );

  const blockStatus = useMemo(
    () => OrderValidationService.checkOrderBlockStatus(orderData),
    [orderData]
  );

  const isBlocked = blockStatus.isBlocked;

  const assignSupplierMutation = useMutation({
    mutationFn: async ({
      supplierId,
      supplierParts,
      supplierName,
      transactionType,
      discount,
      creditNotePercentage,
      splitSelections,
      cptGivenTime,
      splitData,
      estimateNo,
    }: AssignSupplierParams): Promise<AssignSupplierResult> => {
      const isRootOrder = orderData._id === orderData.rootOrderId;
      if (orderData?.requireEstimate && isRootOrder) {
        const cptHoursOffsetBySlab = parseCptHoursOffset(
          mobileConfigs?.logistics_cpt_hours_offset
        );

        const supplierBucket = [
          {
            supplierId,
            supplierName,
            selectedParts: supplierParts as SupplierPart[],
            selectedAt: new Date(),
          },
        ];

        const allocationResult = await EstimateAllocatorService.allocate(
          {
            sourceOrder: orderData,
            suppliers: supplierBucket,
            transactionType,
            orderEta,
            orderItems,
            discount,
            creditNotePercentage,
            cptHoursOffsetBySlab,
            splitSelections: splitSelections || [],
            cptGivenTime: cptGivenTime || new Date().toISOString(),
            splitData: splitData || [],
            estimateNo: estimateNo || "",
          },
          isCustomerOrderFreezed
        );

        if (!allocationResult.success) {
          return {
            success: false,
            message: allocationResult.message || "Estimate allocation failed",
            error: allocationResult.error,
          };
        }

        return {
          success: true,
          message: allocationResult.message,
          allocationResponse: allocationResult,
        };
      }

      return await AssignSupplierService.assignSupplierWithFulfillmentLogic(
        orderId,
        supplierId,
        orderData,
        orderItems,
        supplierParts || [],
        setOrderMetaInfoWithOrderId,
        supplierName
      );
    },
    onSuccess: (result: AssignSupplierResult) => {
      if (result.success) {
        setAssignedSupplier(result.allocationResponse?.allocatedTo || result.allocationResponse?.supplierId);
        refetchAllOrders();

        garaazToast({
          title: "Processed Successfully",
          status: "success",
        });
      } else {
        let errorFromResponse;
        try {
          errorFromResponse = JSON.parse(result.message);
        } catch (e) {
          errorFromResponse = [];
        }

        const title = !!errorFromResponse?.length
          ? errorFromResponse[0]?.title
          : typeof result.message === "string"
          ? result.message
          : OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;

        garaazToast({
          title: title,
          status: "error",
          render: <OrderStatusChangeErrorBox errorMsg={errorFromResponse} />,
        });
        refetchAllOrders();
      }
    },
    onError: (error: any) => {
      const errorFromResponse = error?.response?.data?.errorMsg;
      const title = !!errorFromResponse?.length
        ? errorFromResponse[0]?.title
        : OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;

      garaazToast({
        title: title,
        status: "error",
        render: <OrderStatusChangeErrorBox errorMsg={errorFromResponse} />,
      });
      refetchAllOrders();
    },
  });

  const assignSupplier = useCallback(
    async (
      supplier: any,
      supplierName: string,
      supplierParts?: SupplierParts[],
      transactionType?: string,
      discount?: number,
      creditNotePercentage?: number,
      splitSelections?: NormalizedDeliverySelection[],
      cptGivenTime?: string,
      splitData?: EnrichedSupplierForDelivery[],
      estimateNo?: string,
    ) => {
      if (isBlocked) {
        toast({
          description:
            blockStatus.reason || "Order is blocked from supplier assignment",
          status: "warning",
        });
        return;
      }

      setIsAssigning(true);
      try {
        await assignSupplierMutation.mutateAsync({
          supplierId: supplier._id,
          supplierParts,
          supplierName,
          transactionType,
          discount,
          creditNotePercentage,
          splitSelections,
          cptGivenTime,
          splitData,
          estimateNo,
        });
      } finally {
        setIsAssigning(false);
      }
    },
    [assignSupplierMutation, isBlocked, blockStatus.reason, toast]
  );

  return {
    assignSupplier,
    isAssigning: isAssigning || assignSupplierMutation.isLoading,
    error: assignSupplierMutation.error,
    isBlocked,
    blockStatus,
  };
};
